// 感測器 ↔ 攝影機關聯（plan/vms-web-v0.md DEV-12）：門磁 / PIR 觸發時要帶哪幾台攝影機的畫面（彈窗、事件卡片）。
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { KinioError } from '@kinio/web-sdk';
import { Dialog } from '../../components/Dialog';
import { useDevices } from '../../lib/useDevices';
import { fullName, type VmsDevice } from '../../lib/devices';
import { linkApi } from '../../lib/planeApi';

export function LinkDialog({ sensor, close }: { sensor: VmsDevice | null; close(): void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { tree } = useDevices();
  const links = useQuery({
    queryKey: ['device-links', sensor?.homeId],
    enabled: !!sensor,
    queryFn: () => linkApi.list(sensor!.apiEndpoint, sensor!.homeId),
  });
  const mine = (links.data?.data ?? []).filter((l) => l.sensor_ref === sensor?.aid);
  const [cams, setCams] = useState<Set<string>>(new Set());
  const [purposes, setPurposes] = useState<Set<string>>(new Set(['popup', 'clip']));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setCams(new Set(mine.map((l) => l.device_ref)));
    if (mine[0]) setPurposes(new Set(mine[0].purposes));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [links.dataUpdatedAt, sensor?.key]);

  const cameras = tree.flatMap((d) => [d, ...d.children]).filter((d) => d.isCamera && d.homeId === sensor?.homeId);
  const version = Math.max(0, ...mine.map((l) => l.version));

  async function save() {
    if (!sensor) return;
    setBusy(true);
    setErr(null);
    try {
      await linkApi.put(sensor.apiEndpoint, sensor.homeId, sensor.aid, { expected_version: version, cameras: [...cams], purposes: [...purposes] });
      await qc.invalidateQueries({ queryKey: ['device-links', sensor.homeId] });
      close();
    } catch (e) {
      setErr(e instanceof KinioError && e.status === 409 ? t('map.conflict') : String((e as Error).message ?? e));
      await links.refetch();
    } finally {
      setBusy(false);
    }
  }

  const toggle = (set: Set<string>, v: string) => {
    const n = new Set(set);
    if (n.has(v)) n.delete(v);
    else n.add(v);
    return n;
  };

  return (
    <Dialog open={!!sensor} title={t('link.title', { name: sensor ? fullName(sensor, tree) : '' })} onClose={close}>
      <div className="form">
        <p className="muted small">{t('link.help')}</p>
        <fieldset className="field">
          <legend>{t('link.cameras')}</legend>
          {cameras.map((c) => (
            <label key={c.key} className="check">
              <input type="checkbox" checked={cams.has(c.aid)} disabled={!links.data?.can_write} onChange={() => setCams((s) => toggle(s, c.aid))} />
              <span className={`dot ${c.presence}`} /> {fullName(c, tree)}
            </label>
          ))}
          {!cameras.length && <p className="muted">{t('link.no_cameras')}</p>}
        </fieldset>
        <fieldset className="field">
          <legend>{t('link.purposes')}</legend>
          {(['popup', 'clip'] as const).map((p) => (
            <label key={p} className="check">
              <input type="checkbox" checked={purposes.has(p)} disabled={!links.data?.can_write} onChange={() => setPurposes((s) => toggle(s, p))} />
              {t(`link.purpose_${p}`)}
            </label>
          ))}
        </fieldset>
        {err && <p className="error">{err}</p>}
        {links.data?.can_write ? (
          <button className="btn btn-primary" onClick={() => void save()} disabled={busy || !purposes.size}>
            {t('common.save')}
          </button>
        ) : (
          <p className="muted small">{t('link.readonly')}</p>
        )}
      </div>
    </Dialog>
  );
}
